import { useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { sageJobSiteService } from "../../services/sageJobSiteService"
import { SageCostCode, SageJobSite } from "../../Interfaces"
import { useSelectedCompany } from "../../context/companyProvider"
import { t } from "../../i18n"
import { SageApiError } from "../../utils/sageApiError"
import { formatSageJobStatus } from "../../utils/sageJobStatus"
import { displayJobCode } from "../../utils/sageCodes"
import CwAddButton from "../construction/CwAddButton"
import CwRowActions from "../construction/CwRowActions"
import CwConfirmDialog from "../construction/CwConfirmDialog"
import JobCodeFormModal from "../construction/JobCodeFormModal"
import JobSiteCodeImport from "./JobSiteCodeImport"

export function JobSiteSecondaryPanels({ jobSite }: { jobSite: SageJobSite }) {
  const { selectedCompany, canManageSageMaster } = useSelectedCompany()
  const companyId = selectedCompany?.id
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<SageCostCode | null>(null)
  const [deleting, setDeleting] = useState<SageCostCode | null>(null)
  const [deleteError, setDeleteError] = useState("")
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)

  const { data: costCodesResponse } = useQuery(
    ["sage-job-site-cost-codes", jobSite.id],
    () => sageJobSiteService.getCostCodes(jobSite.id),
    { enabled: !!jobSite.id, suspense: false, useErrorBoundary: false },
  )

  const { data: rosterResponse } = useQuery(
    ["sage-job-site-roster-today", jobSite.id],
    () => sageJobSiteService.getTodayRoster(jobSite.id),
    { enabled: !!jobSite.id, suspense: false, useErrorBoundary: false },
  )

  const cityLine = [jobSite.city, jobSite.state].filter(Boolean).join(", ")
  const cityZip = [cityLine, jobSite.zip].filter(Boolean).join(" ")
  const costCodes = costCodesResponse?.data || []

  const refetchCodes = () => {
    queryClient.invalidateQueries(["sage-job-site-cost-codes", jobSite.id])
    queryClient.invalidateQueries(["sage-cost-codes", companyId])
  }

  const handleDelete = async () => {
    if (!companyId || !deleting) return
    setDeleteSubmitting(true)
    setDeleteError("")
    try {
      await sageJobSiteService.deleteCostCode(companyId, deleting.id)
      setDeleting(null)
      refetchCodes()
    } catch (deleteErr) {
      setDeleteError(deleteErr instanceof SageApiError && deleteErr.message
        ? deleteErr.message
        : t("jobCodes.loadFailed"))
    } finally {
      setDeleteSubmitting(false)
    }
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="text-sm text-gray-600 px-1">
        {jobSite.address && <span>{jobSite.address}{cityZip ? ` · ${cityZip}` : ""}</span>}
        {jobSite.status != null && (
          <span>{jobSite.address ? " · " : ""}Status: {formatSageJobStatus(jobSite.status)}</span>
        )}
        {jobSite.supervisor && <span> · Supervisor: {jobSite.supervisor}</span>}
        {jobSite.sample && <span> · Sample Sage data</span>}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="bg-white shadow-lg rounded-2xl p-4">
          <h3 className="text-lg font-semibold mb-3">Today&apos;s roster</h3>
          <RosterGroup title="Clocked in" people={rosterResponse?.clockedIn} />
          <RosterGroup title="Scheduled" people={rosterResponse?.scheduled} />
          <RosterGroup title="Available" people={rosterResponse?.available} />
        </section>

        <section className="bg-white shadow-lg rounded-2xl p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold">{t("jobCodes.title")}</h3>
            {canManageSageMaster && companyId ? (
              <CwAddButton
                label={t("jobCodes.add")}
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              />
            ) : null}
          </div>
          {canManageSageMaster && companyId ? (
            <JobSiteCodeImport
              companyId={companyId}
              jobSiteId={jobSite.id}
              onImported={refetchCodes}
            />
          ) : null}
          {costCodes.length === 0 ? (
            <p className="text-sm text-gray-500">{t("jobCodes.empty")}</p>
          ) : (
            <table className="w-full text-sm text-start">
              <thead>
                <tr className="bg-secondary-extra-light">
                  <th className="px-3 py-2 text-start">{t("jobCodes.columnCode")}</th>
                  <th className="px-3 py-2 text-start">{t("jobCodes.columnName")}</th>
                  {canManageSageMaster ? (
                    <th className="px-3 py-2 text-start">{t("common.actions")}</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {costCodes.map((code) => (
                  <tr key={code.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{displayJobCode(code.code)}</td>
                    <td className="px-3 py-2">{code.name}</td>
                    {canManageSageMaster ? (
                      <td className="px-3 py-2">
                        <CwRowActions
                          onEdit={() => {
                            setEditing(code)
                            setFormOpen(true)
                          }}
                          onDelete={() => {
                            setDeleteError("")
                            setDeleting(code)
                          }}
                        />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      {companyId ? (
        <JobCodeFormModal
          isOpen={formOpen}
          companyId={companyId}
          jobCode={editing}
          jobSites={[jobSite]}
          lockedJobSiteId={jobSite.id}
          onClose={() => {
            setFormOpen(false)
            setEditing(null)
          }}
          onSaved={refetchCodes}
        />
      ) : null}
      <CwConfirmDialog
        isOpen={Boolean(deleting)}
        title={t("jobCodes.deleteTitle")}
        message={t("jobCodes.deleteConfirm")}
        error={deleteError}
        submitting={deleteSubmitting}
        onConfirm={() => void handleDelete()}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleting(null)
            setDeleteError("")
          }
        }}
      />
    </div>
  )
}

function RosterGroup({
  title,
  people,
}: {
  title: string
  people?: Array<{
    id: number
    employeeNum: number
    firstName: string
    lastName: string
    jobDescription?: string | null
    workingElsewhere?: boolean
    otherJobSiteName?: string | null
  }>
}) {
  const list = people || []
  return (
    <div className="mb-4">
      <h4 className="font-semibold mb-2">{title} ({list.length})</h4>
      {list.length === 0 ? (
        <p className="text-sm text-gray-500">None</p>
      ) : (
        <ul className="text-sm space-y-1">
          {list.map((person) => (
            <li key={person.id}>
              {`${person.firstName || ""} ${person.lastName || ""}`.trim() || `Employee ${person.employeeNum}`}
              {person.jobDescription ? ` — ${person.jobDescription}` : ""}
              {person.workingElsewhere && person.otherJobSiteName
                ? ` (working at ${person.otherJobSiteName})`
                : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
